import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Alert, Box, Button, IconButton, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Card, PageHero, Section } from "../components/ui";
import { useCart } from "../components/CartContext";
import { useAuth0 } from "../hooks/useAuth0";
import { createOrder, errorMessage } from "../services/veysoApi";
import { tokens } from "../theme";

export default function Cart() {
  const { items, total, remove, clear } = useCart();
  const { isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const checkout = async () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setBusy(true);
    setError("");
    try {
      // Only product ids and quantities are sent. The server looks up the prices.
      const created = await createOrder(items.map((i) => ({ productId: i.id, qty: i.qty })));
      setOrder(created);
      clear();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHero eyebrow="Cart" title="Your cart" />
      <Section tone="white" narrow>
        {order ? (
          <Alert severity="success">
            Order #{order.id} created for ${(order.totalCents / 100).toFixed(2)}. Payment is not connected yet, so nothing was charged.
          </Alert>
        ) : items.length === 0 ? (
          <Card><Typography>Your cart is empty.</Typography><Button component={RouterLink} to="/pricing" sx={{ mt: 2 }} variant="contained">See membership</Button></Card>
        ) : (
          <Card>
            {items.map((i) => (
              <Box key={i.id} sx={{ display: "flex", alignItems: "center", py: 1.6, borderBottom: `1px solid ${tokens.line}` }}>
                <Typography sx={{ flex: 1, fontWeight: 500 }}>{i.name}{i.qty > 1 ? ` × ${i.qty}` : ""}</Typography>
                <Typography sx={{ mr: 1 }}>${i.price * i.qty}</Typography>
                <IconButton aria-label={`Remove ${i.name}`} onClick={() => remove(i.id)}><DeleteOutlineIcon /></IconButton>
              </Box>
            ))}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2.5 }}>
              <Typography sx={{ fontWeight: 600 }}>Total</Typography><Typography sx={{ fontWeight: 600 }}>${total}</Typography>
            </Box>
            <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button variant="contained" size="large" onClick={checkout} disabled={busy}>
                {isAuthenticated ? (busy ? "Placing order..." : "Place order") : "Log in to check out"}
              </Button>
              <Button onClick={clear}>Clear cart</Button>
            </Box>
            {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
          </Card>
        )}
      </Section>
    </>
  );
}
